import { API_ERROR_NAMES } from "@coffitivity/shared";
import { markClientOutdated } from "./clientOutdated";

export type TokenProvider = () => Promise<string | undefined>;

export interface IApiClient {
	fetch(url: string, init?: RequestInit): Promise<Response>;
	/** Return the most recently cached token synchronously (no async). */
	getCachedToken(): string | undefined;
}

const REFRESH_BUFFER_MS = 120_000; // 2 minutes

function getJwtExpiryMs(token: string): number {
	try {
		const payload = JSON.parse(atob(token.split(".")[1]));
		return payload.exp * 1000;
	} catch {
		return 0; // unparseable token — force fresh fetch
	}
}

export class ApiClient implements IApiClient {
	private cachedToken: string | undefined;
	private tokenExpiresAt = 0;
	private refreshPromise: Promise<string | undefined> | null = null;
	private getAccessToken: TokenProvider;
	private refreshAccessToken: TokenProvider;

	constructor(
		getAccessToken: TokenProvider,
		refreshAccessToken: TokenProvider,
	) {
		this.getAccessToken = getAccessToken;
		this.refreshAccessToken = refreshAccessToken;
	}

	getCachedToken(): string | undefined {
		return this.cachedToken;
	}

	async fetch(url: string, init?: RequestInit): Promise<Response> {
		const token = await this.resolveToken();
		let response = await this.doFetch(url, init, token);

		if (response.status === 401 && token) {
			const newToken = await this.forceRefresh();
			if (newToken && newToken !== token) {
				response = await this.doFetch(url, init, newToken);
			}
		}

		await this.flagOutdatedClient(response);
		return response;
	}

	/**
	 * Central reactive trigger for the outdated-client signal. Any endpoint that
	 * rejects a superseded request with `CLIENT_OUTDATED` surfaces the same
	 * refresh prompt, with no per-caller wiring. Inspects a clone so the caller's
	 * body stays readable, and only on error responses to keep the cost off the
	 * happy path.
	 */
	private async flagOutdatedClient(response: Response): Promise<void> {
		if (response.ok) return;
		try {
			const body = (await response.clone().json()) as { code?: string };
			if (body?.code === API_ERROR_NAMES.CLIENT_OUTDATED) {
				markClientOutdated();
			}
		} catch {
			// Non-JSON or unreadable body — nothing to signal.
		}
	}

	private async resolveToken(): Promise<string | undefined> {
		if (
			this.cachedToken &&
			Date.now() < this.tokenExpiresAt - REFRESH_BUFFER_MS
		) {
			return this.cachedToken;
		}

		const token = await this.getAccessToken();
		if (token) {
			this.cachedToken = token;
			this.tokenExpiresAt = getJwtExpiryMs(token);
		}
		return token;
	}

	/**
	 * Force a token refresh via refreshAccessToken. Deduplicates concurrent
	 * calls so only one refresh request is in-flight at a time.
	 */
	private async forceRefresh(): Promise<string | undefined> {
		if (!this.refreshPromise) {
			this.refreshPromise = this.refreshAccessToken().then((token) => {
				if (token) {
					this.cachedToken = token;
					this.tokenExpiresAt = getJwtExpiryMs(token);
				}
				this.refreshPromise = null;
				return token;
			});
		}
		return this.refreshPromise;
	}

	private doFetch(
		url: string,
		init: RequestInit | undefined,
		token: string | undefined,
	): Promise<Response> {
		const headers = new Headers(init?.headers);

		if (!headers.has("Content-type")) {
			headers.set("Content-type", "application/json; charset=UTF-8");
		}
		if (token) {
			headers.set("Authorization", `Bearer ${token}`);
		}

		return globalThis.fetch(url, { ...init, headers });
	}
}
